import React from 'react';
import styled from 'styled-components';
import * as CONSTANTS from '../constants';
import Link from 'next/link';
import smoothscroll from 'smoothscroll-polyfill';
import { ModalContext } from '../context/modal';
import { logEvent } from '../utils/analytics';

const ToTop = styled.button`
  position: fixed;
  bottom: 15px;
  right: 20px;
  font-size: 36px;
  color: #ccc;
  display: none;
  ${props => (props.isUpButtonVisible) ? `display: block;` : ''}

  &:hover {
    color: #ddd;
  }
`;

const FooterLinks = styled.nav`
  text-align: center;
  display: table;
  margin: 0 auto;

  &:last-of-type {
    margin-bottom: 30px;
  }
`;

const footerLinkStyles = `
  color: ${CONSTANTS.darkGray};
  font-weight: 200;
  font-size: 12px;
  text-align: center;
  margin: 0 8px;
  position: relative;
  cursor: pointer;

  &:hover {
    color: ${CONSTANTS.mediumGray};
  }
`;

const FooterLink = styled.a`
  ${footerLinkStyles};
`;

const FooterButton = styled.button`
  ${footerLinkStyles};
`;

const Copyright = styled.p`
  font-size: 12px;
  text-align: center;
  padding-top: 5px;
  color: ${CONSTANTS.mediumGray};
`;

const Ads = styled.div`
  width: 100%;
  height: 120px;
  border-top: 4px solid #f4f4f4;
  filter: grayscale(1);

  display: flex;
  align-items: center;
  justify-content: center;
`;

const SponsoredBy = styled.h5`
  color: #777;
  font-size: 12px;
  text-align: center;
`;

const SponsorLogo = styled.img`
  margin: 10px auto 0 auto;
`;

const VerticalAlign = styled.div`
  width: 100%;
`;

const LogoWrapper = styled.div`
  text-align: center;
`;

const Item = styled.a`
  font-weight: bold;
  margin-right: 30px;
  color: #989898;
  vertical-align: baseline;

  &:last-of-type {
    margin-right: 0;
  }
`;

const FooterWrap = styled.footer`
  width: 100%;

  ${FooterLink} {
    ${props => (props.darkMode === true) ? `
      color: ${CONSTANTS.darkModeAppleGrayText};

      &:hover {
        color: #444;
      }
    ` : ''}
  }

  ${FooterButton} {
    ${props => (props.darkMode === true) ? `
      color: ${CONSTANTS.darkModeAppleGrayText};

      &:hover {
        color: #444;
      }
    ` : ''}
  }

  ${Copyright} {
    ${props => (props.darkMode === true) ? `
    color: ${CONSTANTS.white};
  ` : ''}
  }

  ${Ads} {
    ${props => (props.darkMode === true) ? `
    border-top: 4px solid #121212;
    filter: 2;
  ` : ''}
  }

  ${SponsoredBy} {
    ${props => (props.darkMode === true) ? `
    color: ${CONSTANTS.white};
    ` : ''}
  }

  ${ToTop} {
    ${props => (props.darkMode === true) ? `
    color: #717170; &:hover {color: #444;}
    ` : ''}
  }
`;

export default class Footer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isUpButtonVisible: false
    };

    this.handleScroll = this.handleScroll.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
    this.showModal = this.showModal.bind(this);
  }

  componentDidMount() {
    this.setState({
      isUpButtonVisible: false
    });

    window.addEventListener('scroll', this.handleScroll);
    smoothscroll.polyfill();
  }

  handleScroll() {
    if(window.scrollY > 1000 && this.state.isUpButtonVisible !== true) {
      this.setState({
        isUpButtonVisible: true,
      });

    } else if(window.scrollY < 1000 && this.state.isUpButtonVisible === true) {
      this.setState({
        isUpButtonVisible: false,
      });
    }
  }

  scrollToTop(e) {
    e.preventDefault();
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    logEvent('scroll to top button', 'click');
  }

  showModal(e, modalName) {
    e.preventDefault();
    this.context.updateValue('currentModal', modalName);
  }

  render() {
    return (
      <FooterWrap darkMode={this.context.darkMode}>
        <FooterLinks>
          <FooterButton onClick={(e) => {this.showModal(e, 'newsletter');logEvent('footer link click', 'subscribe')}}>Newsletter</FooterButton>
          <FooterLink target="_blank" rel="noopener noreferrer" href="https://twitter.com/siteseegallery" onClick={(e) => {logEvent('footer link click', 'twitter')}}>Twitter</FooterLink>
          <FooterLink target="_blank" rel="noopener noreferrer" href="https://chrome.google.com/webstore/detail/sitesee-web-design-inspir/hnpkjoifldafgenlhognhkfjbpglbmmp" onClick={(e) => {logEvent('footer link click', 'chrome extension')}}>Chrome Extension</FooterLink>
          <FooterLink href="mailto:yourfriends@sitesee.com" onClick={(e) => {logEvent('footer link click', 'advertise')}}>Advertise</FooterLink>
        </FooterLinks>
        <Copyright>All screenshots &copy; their respective owners.</Copyright>
        <FooterLinks>
          <Link
              href="/legal?type=privacy"
              as="/privacy"
          >
            <FooterLink href="/privacy">Privacy</FooterLink>
          </Link>
          <Link
              href="/legal?type=terms"
              as="/terms"
          >
            <FooterLink href="/terms">Terms</FooterLink> 
          </Link>
          <FooterLink href="/feed" target="_blank" rel="noopener noreferrer" onClick={(e) => {logEvent('footer link click', 'rss feed')}}>RSS</FooterLink>
        </FooterLinks>
        <Ads>
          <VerticalAlign>
            <SponsoredBy>Sponsored by</SponsoredBy>
            <LogoWrapper>
              <Item href="https://m.do.co/c/66032930b4c4" onClick={(e) => {logEvent('footer advertising click', 'Digital Ocean')}} target="_blank" rel="noopener noreferrer"><SponsorLogo height="22" alt="Sponsored by Digital Ocean" src={this.context.darkMode ? 'https://d3alngem7je9z2.cloudfront.net/DO_Logo-White.svg' : 'https://d3alngem7je9z2.cloudfront.net/DO_Logo.svg'}/></Item>
              <Item href="https://pastel.grsm.io/sitesee" onClick={(e) => {logEvent('footer advertising click', 'Pastel')}} target="_blank" rel="noopener noreferrer"><SponsorLogo height="22" alt="Sponsored by Pastel" src={this.context.darkMode ? 'https://d3alngem7je9z2.cloudfront.net/PastelLogo-White.svg' : 'https://d3alngem7je9z2.cloudfront.net/PastelLogo.svg'} /></Item>
            </LogoWrapper>
          </VerticalAlign>
        </Ads>
        <ToTop aria-label="Scroll to top" isUpButtonVisible={this.state.isUpButtonVisible} onClick={(e) => this.scrollToTop(e)}>&#8613;</ToTop>
      </FooterWrap>
    )
  }
}

Footer.contextType = ModalContext;
